import { useEffect, useState } from "react";
import type { AnyPublishedAiResearchDaily, IngestionBatchListResponse, Phase0HealthResponse, PublishedRealtimeMarketResponse, PublishedStockTrackingResponse, PublishedWorkspaceResponse } from "@ashare/contracts";
import { fetchAiResearchDaily, fetchHealth, fetchIngestionBatches, fetchRealtimeMarket, fetchStockTracking, fetchWorkspace, retryFailedIngestionShards } from "./api.js";
import { isRouteId, NAV_ITEMS, type RouteId } from "./navigation.js";
import { AlertsPage, ArchivePage, CandidatePage, MarketPage, MethodsPage, QualityPage, ScanPage } from "./pages.js";
import { AiEvidencePage, AiMarketPage, AiResearchOverviewPage, AiSectorsPage, AiStocksPage, TrackingPage } from "./ai-pages.js";
import { PublicHomePage } from "./public-home.js";
import { ArticleIndexPage, ArticlePage, PrivacyPage, WorkspaceUnavailablePage } from "./public-articles.js";

type SiteRoute = "home" | "articles" | "privacy" | `article/${string}` | RouteId;
const PUBLISHED_RESEARCH_REFRESH_MS = 60_000;
const PRIVATE_WORKSPACE_ENABLED = import.meta.env.VITE_PRIVATE_WORKSPACE_ENABLED === "true";

function NavIcon({ id }: { id: RouteId }): React.JSX.Element {
  const paths: Record<RouteId, React.JSX.Element> = {
    market: <><path d="M4 19V9" /><path d="M10 19V5" /><path d="M16 19v-7" /><path d="M3 19h18" /></>,
    scan: <><path d="M4 6h16" /><path d="M7 12h10" /><path d="M10 18h4" /></>,
    candidates: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /><path d="M8 11h6M11 8v6" /></>,
    "ai-lab": <><path d="M9 3h6" /><path d="M10 9v-4h4v4l5 9a2 2 0 0 1-2 3H7a2 2 0 0 1-2-3Z" /><path d="M8 15h8" /></>,
    "ai-market": <><path d="M4 18V9" /><path d="M10 18V5" /><path d="M16 18v-6" /><path d="m3 7 5-3 5 4 8-5" /></>,
    "ai-sectors": <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
    "ai-stocks": <><path d="M4 19V7" /><path d="m4 15 5-5 4 3 7-8" /><path d="M15 5h5v5" /></>,
    tracking: <><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7 15 4-4 3 2 5-7" /><circle cx="19" cy="6" r="1.5" /></>,
    methods: <><path d="M5 4h14v16H5z" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
    archive: <><path d="M4 7h16v13H4z" /><path d="M3 4h18v3H3z" /><path d="M9 11h6" /></>,
    alerts: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
    quality: <><path d="M12 3 4 7v5c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V7Z" /><path d="m9 12 2 2 4-5" /></>,
    "ai-evidence": <><path d="M5 3h11l3 3v15H5z" /><path d="M16 3v4h4" /><path d="M8 11h8M8 15h8M8 19h5" /></>
  };
  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[id]}</svg>;
}

function currentRoute(): SiteRoute {
  const value = window.location.hash.replace(/^#\/?/, "").split("?")[0] ?? "";
  if (value === "home" || value === "") return "home";
  if (value === "articles") return "articles";
  if (value === "privacy") return "privacy";
  if (value.startsWith("articles/") && value.length > "articles/".length) return `article/${value.slice("articles/".length)}`;
  return isRouteId(value) ? value : "home";
}

function renderPage(
  route: RouteId,
  workspace: PublishedWorkspaceResponse,
  ingestionBatches: IngestionBatchListResponse | null,
  ingestionError: string | null,
  retryingPlanId: string | null,
  onRetryFailed: (planId: string) => Promise<void>,
  aiResearch: AnyPublishedAiResearchDaily | null,
  aiResearchError: string | null,
  stockTracking: PublishedStockTrackingResponse | null,
  stockTrackingError: string | null,
  realtimeMarket: PublishedRealtimeMarketResponse | null,
  realtimeMarketError: string | null
): React.JSX.Element {
  switch (route) {
    case "market": return <MarketPage workspace={workspace} />;
    case "scan": return <ScanPage workspace={workspace} />;
    case "candidates": return <CandidatePage workspace={workspace} />;
    case "ai-lab": return <AiResearchOverviewPage workspace={workspace} aiResearch={aiResearch} aiResearchError={aiResearchError} />;
    case "ai-market": return <AiMarketPage workspace={workspace} aiResearch={aiResearch} aiResearchError={aiResearchError} />;
    case "ai-sectors": return <AiSectorsPage workspace={workspace} aiResearch={aiResearch} aiResearchError={aiResearchError} />;
    case "ai-stocks": return <AiStocksPage workspace={workspace} aiResearch={aiResearch} aiResearchError={aiResearchError} />;
    case "tracking": return <TrackingPage workspace={workspace} tracking={stockTracking} trackingError={stockTrackingError} realtimeMarket={realtimeMarket} realtimeMarketError={realtimeMarketError} />;
    case "ai-evidence": return <AiEvidencePage workspace={workspace} aiResearch={aiResearch} aiResearchError={aiResearchError} />;
    case "methods": return <MethodsPage workspace={workspace} />;
    case "archive": return <ArchivePage workspace={workspace} />;
    case "alerts": return <AlertsPage workspace={workspace} />;
    case "quality": return <QualityPage workspace={workspace} ingestionBatches={ingestionBatches} ingestionError={ingestionError} retryingPlanId={retryingPlanId} onRetryFailed={onRetryFailed} />;
  }
}

function LoadingState({ error }: { error: string | null }): React.JSX.Element {
  if (error) return <section className="error-state"><b>研究快照加载失败</b><p>{error}</p><button type="button" onClick={() => window.location.reload()}>重新加载</button></section>;
  return <section className="loading-state"><i /><b>正在读取 Published 研究快照…</b></section>;
}

export function App(): React.JSX.Element {
  const [route, setRoute] = useState<SiteRoute>(currentRoute);
  const [menuOpen, setMenuOpen] = useState(false);
  const [workspace, setWorkspace] = useState<PublishedWorkspaceResponse | null>(null);
  const [health, setHealth] = useState<Phase0HealthResponse | null>(null);
  const [ingestionBatches, setIngestionBatches] = useState<IngestionBatchListResponse | null>(null);
  const [ingestionError, setIngestionError] = useState<string | null>(null);
  const [retryingPlanId, setRetryingPlanId] = useState<string | null>(null);
  const [aiResearch, setAiResearch] = useState<AnyPublishedAiResearchDaily | null>(null);
  const [aiResearchError, setAiResearchError] = useState<string | null>(null);
  const [stockTracking, setStockTracking] = useState<PublishedStockTrackingResponse | null>(null);
  const [stockTrackingError, setStockTrackingError] = useState<string | null>(null);
  const [realtimeMarket, setRealtimeMarket] = useState<PublishedRealtimeMarketResponse | null>(null);
  const [realtimeMarketError, setRealtimeMarketError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const workspaceRoute = PRIVATE_WORKSPACE_ENABLED && isRouteId(route);

  useEffect(() => {
    const onHashChange = (): void => {
      const hash = window.location.hash;
      if (hash && !hash.startsWith("#/")) return;
      setRoute(currentRoute());
      setMenuOpen(false);
      window.scrollTo({ top: 0 });
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!workspaceRoute) return;
    let active = true;
    fetchWorkspace().then((value) => { if (active) setWorkspace(value); }).catch((reason: unknown) => { if (active) setError(reason instanceof Error ? reason.message : "加载失败"); });
    fetchHealth().then((value) => { if (active) setHealth(value); }).catch(() => { if (active) setHealth(null); });
    fetchIngestionBatches().then((value) => { if (active) { setIngestionBatches(value); setIngestionError(null); } }).catch((reason: unknown) => { if (active) setIngestionError(reason instanceof Error ? reason.message : "批次状态加载失败"); });
    return () => { active = false; };
  }, [workspaceRoute]);

  useEffect(() => {
    if (!workspaceRoute) return;
    let active = true;
    const refreshPublishedResearch = (): void => {
      fetchAiResearchDaily().then((value) => {
        if (active) { setAiResearch(value); setAiResearchError(null); }
      }).catch((reason: unknown) => {
        if (active) setAiResearchError(reason instanceof Error ? reason.message : "AI研究结果加载失败");
      });
      fetchStockTracking().then((value) => {
        if (active) { setStockTracking(value); setStockTrackingError(null); }
      }).catch((reason: unknown) => {
        if (active) setStockTrackingError(reason instanceof Error ? reason.message : "持续跟踪加载失败");
      });
    };
    const refreshWhenVisible = (): void => { if (document.visibilityState === "visible") refreshPublishedResearch(); };
    refreshPublishedResearch();
    const timer = window.setInterval(refreshPublishedResearch, PUBLISHED_RESEARCH_REFRESH_MS);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [workspaceRoute]);

  useEffect(() => {
    if (!workspaceRoute || route !== "tracking") return;
    let active = true;
    const refresh = (): void => {
      fetchRealtimeMarket().then((value) => {
        if (active) { setRealtimeMarket(value); setRealtimeMarketError(null); }
      }).catch((reason: unknown) => {
        if (active) setRealtimeMarketError(reason instanceof Error ? reason.message : "盘中行情预览加载失败");
      });
    };
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [route, workspaceRoute]);

  const retryFailed = async (planId: string): Promise<void> => {
    setRetryingPlanId(planId);
    setIngestionError(null);
    try {
      await retryFailedIngestionShards(planId);
      setIngestionBatches(await fetchIngestionBatches());
    } catch (reason) {
      setIngestionError(reason instanceof Error ? reason.message : "失败分片重置失败");
    } finally {
      setRetryingPlanId(null);
    }
  };

  const liveDevelopment = workspace?.meta.dataMode === "development" && workspace.meta.sourceRefs.some((source) => source.sourceType === "licensed_provider");
  const dataModeLabel = workspace?.meta.dataMode === "development"
    ? liveDevelopment ? "本地开发真实样本" : "本地测试数据"
    : workspace?.meta.dataMode === "production" ? "正式数据" : "演示数据";

  if (route === "home") {
    return <div className="public-shell"><a className="skip-link" href="#main-content">跳到正文</a><PublicHomePage /></div>;
  }
  if (route === "articles") return <ArticleIndexPage />;
  if (route === "privacy") return <PrivacyPage />;
  if (route.startsWith("article/")) return <ArticlePage slug={route.slice("article/".length)} />;
  if (!isRouteId(route)) return <ArticlePage slug="" />;
  if (!PRIVATE_WORKSPACE_ENABLED) return <WorkspaceUnavailablePage />;

  return (
    <div className="research-app">
      <a className="skip-link" href="#main-content">跳到正文</a>
      {menuOpen && <button className="workspace-scrim" aria-label="关闭导航" onClick={() => setMenuOpen(false)} />}
      <aside className={menuOpen ? "workspace-sidebar is-open" : "workspace-sidebar"} aria-label="研究工作台导航">
        <div className="workspace-brand"><a href="#/home" aria-label="返回云宸LAB首页"><span>云</span><b>云宸LAB<small>Research Workspace</small></b></a><button type="button" aria-label="关闭导航" onClick={() => setMenuOpen(false)}>×</button></div>
        <nav className="workspace-nav-groups">
          {(["core", "tools", "system"] as const).map((section) => <section key={section}><div className="workspace-nav-label">{section === "core" ? "核心研究" : section === "tools" ? "个人工具" : "系统与审计"}</div>{NAV_ITEMS.filter((item) => item.section === section && item.showInSidebar).map((item) => <a key={item.id} className={route === item.id ? "active" : ""} href={`#/${item.id}`}><span><NavIcon id={item.id} /></span><b>{item.label}</b></a>)}</section>)}
        </nav>
        <div className="workspace-profile"><span>云</span><div><b>云宸研究空间</b><small>{dataModeLabel}</small></div></div>
      </aside>
      <div className="workspace-main">
        <header className="workspace-topbar">
          <button className="workspace-menu-button" type="button" aria-label="打开导航" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h16" /></svg></button>
          <div><small>RESEARCH WORKSPACE</small><b>{NAV_ITEMS.find((item) => item.id === route)?.label}</b></div>
          <div className="workspace-top-actions"><span className={`health-chip health-${health?.status ?? "loading"}`}><i />{health?.status ?? "连接中"}</span><a href="#/home">返回网站首页</a></div>
        </header>
        <main className="workspace-content" id="main-content">
          {error && <LoadingState error={error} />}
          {!error && !workspace && <LoadingState error={null} />}
          {workspace && renderPage(route, workspace, ingestionBatches, ingestionError, retryingPlanId, retryFailed, aiResearch, aiResearchError, stockTracking, stockTrackingError, realtimeMarket, realtimeMarketError)}
        </main>
      </div>
    </div>
  );
}
